import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleError, handleResponse } from "../utils/handleResponses";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import EndpointFunctions from "../utils/Functions/endpointFunctions";
import { message } from "antd";
import Endpoints from "../api/endpoints";

const usePersonnelAdvertService = () => {
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  const useGetAdvertById = (advertId) => {
    return useQuery({
      queryKey: ["advert", advertId],
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAdvertPersonnel,
          axiosPrivate: axiosPrivate,
          httpParams: { id: advertId },
        });
        return handleResponse({ response });
      },
    });
  };

  const useGetSimilarAdverts = (advertId) => {
    return useQuery({
      queryKey: ["similarAdverts", advertId],
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getSimilarAdverts,
          axiosPrivate: axiosPrivate,
          httpParams: { advertId: advertId, page: 0, size: 8 },
        });
        return handleResponse({ response });
      },
    });
  };

  const useFilterAdvert = () => {
    return useMutation({
      mutationFn: async (requestBody) => {
        const response = await EndpointFunctions.postData({
          endpointURL: Endpoints.filterAdvert,
          axiosPrivate: axiosPrivate,
          requestBody: requestBody,
        });
        return handleResponse({ response });
      },
      onError: (error) => {
        message.error(handleError({ error }).errorMessage);
      },
    });
  };

  const useGetAdvertsByLabelKey = (labelKey) => {
    return useQuery({
      queryKey: ["advertsByLabelKey", labelKey],
      queryFn: async () => {
        const response = await EndpointFunctions.postData({
          endpointURL: Endpoints.filterAdvert,
          axiosPrivate: axiosPrivate,
          requestBody: {
            fields: {
              labelKey: labelKey,
            },
            pagination: {
              page: 0,
              size: 4,
            },
          },
        });
        return handleResponse({ response });
      },
      enabled: !!labelKey,
      staleTime: 5 * 60 * 1000,
    });
  };

  const useApplyAdvertPersonnel = () => {
    return useMutation({
      mutationFn: async (requestBody) => {
        const response = await EndpointFunctions.postData({
          endpointURL: Endpoints.applyAdvertPersonnel,
          axiosPrivate: axiosPrivate,
          requestBody: requestBody,
        });
        return handleResponse({ response });
      },
      onSuccess: () => {
        message.success("İlana Başarıyla Başvurdunuz");
        // Invalidate the advert query to refresh the data
        queryClient.invalidateQueries({ queryKey: ["advert"] });
      },
      onError: (error) => {
        message.error(handleError({ error }).errorMessage);
      },
    });
  };

  const useFavoriteAdvertPersonnel = () => {
    return useMutation({
      mutationFn: async (advertId) => {
        const response = await EndpointFunctions.postData({
          endpointURL: Endpoints.favoriteAdvertPersonnel,
          axiosPrivate: axiosPrivate,
          requestBody: { advertId },
        });
        return { advertId, response: handleResponse({ response }) };
      },
      onSuccess: ({ advertId }) => {
        console.log("Refetching for advertId:", advertId);
        message.success("İlan Favorilere Başarıyla Eklendi");
        queryClient.invalidateQueries(["advert", advertId]);
      },
      onError: (error) => {
        message.error(handleError({ error }).errorMessage);
      },
    });
  };

  const useUnfavoriteAdvertPersonnel = () => {
    return useMutation({
      mutationFn: async (advertId) => {
        const response = await EndpointFunctions.postData({
          endpointURL: Endpoints.unfavoriteAdvertPersonnel,
          axiosPrivate: axiosPrivate,
          requestBody: { advertId },
        });
        return { advertId, response: handleResponse({ response }) };
      },
      onSuccess: ({ advertId }) => {
        console.log("Refetching for advertId after unfavorite:", advertId);
        message.success("İlan Favorilerden Çıkarıldı");
        queryClient.invalidateQueries(["advert", advertId]);
      },
      onError: (error) => {
        message.error(handleError({ error }).errorMessage);
      },
    });
  };

  const useGetFavoriteAdverts = (pagination) => {
    return useQuery({
      queryKey: ["FavoriteAdverts", pagination.page, pagination.size],
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getFavoriteAdverts,
          axiosPrivate: axiosPrivate,
          httpParams: {
            page: pagination.page,
            size: pagination.size,
          },
        });
        return handleResponse({ response });
      },
      keepPreviousData: true, // Keep old data visible while new data is loading
    });
  };
  const useGetAppliedAdverts = (requestBody) => {
    return useQuery({
      queryKey: ["AppliedAdverts"],
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAppliedAdverts,
          axiosPrivate: axiosPrivate,
          httpParams: requestBody,
        });
        return handleResponse({ response });
      },
    });
  };
  const useGetAssignedAdverts = (requestBody) => {
    return useQuery({
      queryKey: ["AssignedAdverts"],
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAssignedAdverts,
          axiosPrivate: axiosPrivate,
          httpParams: requestBody,
        });
        return handleResponse({ response });
      },
    });
  };

  return {
    useGetAdvertById,
    useFilterAdvert,
    useFavoriteAdvertPersonnel,
    useUnfavoriteAdvertPersonnel,
    useApplyAdvertPersonnel,
    useGetSimilarAdverts,
    useGetFavoriteAdverts,
    useGetAssignedAdverts,
    useGetAppliedAdverts,
    useGetAdvertsByLabelKey,
  };
};

export default usePersonnelAdvertService;
